import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Loader2, Globe, Calendar, User, DollarSign, Hash, AlertTriangle, GitMerge } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { MissingLanguagesTable } from './MissingLanguagesTable';
import { MissingPostingDateTable } from './MissingPostingDateTable';
import { MissingKolDbIdTable } from './MissingKolDbIdTable';
import { MissingExpenseTable } from './MissingExpenseTable';
import { MissingContentIdTable } from './MissingContentIdTable';
import { OrganicFreeExpenseTable } from './OrganicFreeExpenseTable';
import { ParentChildRelationshipManager } from './ParentChildRelationshipManager';

interface DataQualityCounts {
  missingLanguages: number;
  missingPostingDates: number;
  missingKolDbIds: number;
  missingExpenses: number;
  missingContentIds: number;
  organicFreeExpenses: number;
  potentialDuplicates: number;
}

export const DataQualityCenter: React.FC = () => {
  const [counts, setCounts] = useState<DataQualityCounts>({
    missingLanguages: 0,
    missingPostingDates: 0,
    missingKolDbIds: 0,
    missingExpenses: 0,
    missingContentIds: 0,
    organicFreeExpenses: 0,
    potentialDuplicates: 0
  });
  const [isLoading, setIsLoading] = useState(true);

  const fetchCounts = async () => {
    try {
      setIsLoading(true);
      
      // Fetch counts for each category in parallel
      const [
        languagesResult, 
        datesResult, 
        kolDbResult, 
        expensesResult, 
        contentIdResult,
        organicFreeResult,
        duplicatesResult
      ] = await Promise.all([
        supabase
          .from('kol_tracker')
          .select('id', { count: 'exact', head: true })
          .or('language.is.null,language.eq.'),
        
        supabase
          .from('kol_tracker')
          .select('id', { count: 'exact', head: true })
          .is('posting_date', null),
        
        supabase
          .from('kol_tracker')
          .select('id', { count: 'exact', head: true })
          .is('kol_db_id', null),
        
        supabase
          .from('kol_tracker')
          .select('id', { count: 'exact', head: true })
          .is('expense', null),

        supabase
          .from('kol_tracker')
          .select('id', { count: 'exact', head: true })
          .or('campaign_bu.is.null,campaign_bu.eq.[]'),

        supabase
          .from('kol_tracker')
          .select('id', { count: 'exact', head: true })
          .eq('expense', 0),

        // Count potential duplicates using the new function
        supabase.rpc('get_potential_duplicates')
      ]);

      // Handle the duplicates result properly
      const duplicatesCount = Array.isArray(duplicatesResult.data) ? duplicatesResult.data.length : 0;

      setCounts({
        missingLanguages: languagesResult.count || 0,
        missingPostingDates: datesResult.count || 0,
        missingKolDbIds: kolDbResult.count || 0,
        missingExpenses: expensesResult.count || 0,
        missingContentIds: contentIdResult.count || 0,
        organicFreeExpenses: organicFreeResult.count || 0,
        potentialDuplicates: duplicatesCount
      });
    } catch (error) {
      console.error('Error fetching data quality counts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCounts();
  }, []);

  const refreshCounts = () => {
    fetchCounts();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading data quality analysis...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border border-red-200 dark:border-red-800">
          <div className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-red-600" />
            <span className="text-sm font-medium">Missing Languages</span>
          </div>
          <div className="text-2xl font-bold text-red-600 mt-1">{counts.missingLanguages}</div>
        </div>
        
        <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg border border-orange-200 dark:border-orange-800">
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-orange-600" />
            <span className="text-sm font-medium">Missing Posting Dates</span>
          </div>
          <div className="text-2xl font-bold text-orange-600 mt-1">{counts.missingPostingDates}</div>
        </div>
        
        <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border border-yellow-200 dark:border-yellow-800">
          <div className="flex items-center gap-2">
            <User className="h-5 w-5 text-yellow-600" />
            <span className="text-sm font-medium">Missing KOL DB IDs</span>
          </div>
          <div className="text-2xl font-bold text-yellow-600 mt-1">{counts.missingKolDbIds}</div>
        </div>
        
        <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
          <div className="flex items-center gap-2">
            <Hash className="h-5 w-5 text-purple-600" />
            <span className="text-sm font-medium">Missing Content IDs</span>
          </div>
          <div className="text-2xl font-bold text-purple-600 mt-1">{counts.missingContentIds}</div>
        </div>
        
        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-blue-600" />
            <span className="text-sm font-medium">Missing Expenses</span>
          </div>
          <div className="text-2xl font-bold text-blue-600 mt-1">{counts.missingExpenses}</div>
        </div>
        
        <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
          <div className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-green-600" />
            <span className="text-sm font-medium">Organic & Free</span>
          </div>
          <div className="text-2xl font-bold text-green-600 mt-1">{counts.organicFreeExpenses}</div>
        </div>
        
        <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg border border-indigo-200 dark:border-indigo-800 col-span-2">
          <div className="flex items-center gap-2">
            <GitMerge className="h-5 w-5 text-indigo-600" />
            <span className="text-sm font-medium">Potential Duplicates</span>
          </div>
          <div className="text-2xl font-bold text-indigo-600 mt-1">{counts.potentialDuplicates}</div>
        </div>
      </div>

      <Tabs defaultValue="languages" className="w-full">
        <TabsList className="grid w-full grid-cols-7 bg-muted/50">
          <TabsTrigger 
            value="languages" 
            className="relative data-[state=active]:bg-background data-[state=active]:shadow-sm"
          >
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4" />
              <span className="hidden sm:inline">Languages</span>
              <span className="sm:hidden">Lang</span>
              {counts.missingLanguages > 0 && (
                <div className="flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-destructive text-destructive-foreground text-xs font-medium rounded-full">
                  {counts.missingLanguages}
                </div>
              )}
            </div>
          </TabsTrigger>
          
          <TabsTrigger 
            value="dates" 
            className="relative data-[state=active]:bg-background data-[state=active]:shadow-sm"
          >
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <span className="hidden sm:inline">Dates</span>
              <span className="sm:hidden">Date</span>
              {counts.missingPostingDates > 0 && (
                <div className="flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-orange-500 text-white text-xs font-medium rounded-full">
                  {counts.missingPostingDates}
                </div>
              )}
            </div>
          </TabsTrigger>
          
          <TabsTrigger 
            value="koldb" 
            className="relative data-[state=active]:bg-background data-[state=active]:shadow-sm"
          >
            <div className="flex items-center gap-2">
              <User className="h-4 w-4" />
              <span className="hidden sm:inline">KOL DB</span>
              <span className="sm:hidden">KOL</span>
              {counts.missingKolDbIds > 0 && (
                <div className="flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-yellow-500 text-white text-xs font-medium rounded-full">
                  {counts.missingKolDbIds}
                </div>
              )}
            </div>
          </TabsTrigger>
          
          <TabsTrigger 
            value="contentid" 
            className="relative data-[state=active]:bg-background data-[state=active]:shadow-sm"
          >
            <div className="flex items-center gap-2">
              <Hash className="h-4 w-4" />
              <span className="hidden sm:inline">Content</span>
              <span className="sm:hidden">Cont</span>
              {counts.missingContentIds > 0 && (
                <div className="flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-purple-500 text-white text-xs font-medium rounded-full">
                  {counts.missingContentIds}
                </div>
              )}
            </div>
          </TabsTrigger>
          
          <TabsTrigger 
            value="expenses" 
            className="relative data-[state=active]:bg-background data-[state=active]:shadow-sm"
          >
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              <span className="hidden sm:inline">Expenses</span>
              <span className="sm:hidden">Exp</span>
              {counts.missingExpenses > 0 && (
                <div className="flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-blue-500 text-white text-xs font-medium rounded-full">
                  {counts.missingExpenses}
                </div>
              )}
            </div>
          </TabsTrigger>
          
          <TabsTrigger 
            value="organic" 
            className="relative data-[state=active]:bg-background data-[state=active]:shadow-sm"
          >
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              <span className="hidden sm:inline">Organic</span>
              <span className="sm:hidden">Org</span>
              {counts.organicFreeExpenses > 0 && (
                <div className="flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-green-500 text-white text-xs font-medium rounded-full">
                  {counts.organicFreeExpenses}
                </div>
              )}
            </div>
          </TabsTrigger>
          
          <TabsTrigger 
            value="relationships" 
            className="relative data-[state=active]:bg-background data-[state=active]:shadow-sm"
          >
            <div className="flex items-center gap-2">
              <GitMerge className="h-4 w-4" />
              <span className="hidden sm:inline">Relations</span>
              <span className="sm:hidden">Rel</span>
              {counts.potentialDuplicates > 0 && (
                <div className="flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-indigo-500 text-white text-xs font-medium rounded-full">
                  {counts.potentialDuplicates}
                </div>
              )}
            </div>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="languages" className="mt-6">
          <MissingLanguagesTable onDataChange={refreshCounts} groupByPic={true} />
        </TabsContent>

        <TabsContent value="dates" className="mt-6">
          <MissingPostingDateTable onDataChange={refreshCounts} groupByPic={true} />
        </TabsContent>

        <TabsContent value="koldb" className="mt-6">
          <MissingKolDbIdTable onDataChange={refreshCounts} groupByPic={true} />
        </TabsContent>

        <TabsContent value="contentid" className="mt-6">
          <MissingContentIdTable onDataChange={refreshCounts} groupByPic={true} />
        </TabsContent>

        <TabsContent value="expenses" className="mt-6">
          <MissingExpenseTable onDataChange={refreshCounts} groupByPic={true} />
        </TabsContent>

        <TabsContent value="organic" className="mt-6">
          <OrganicFreeExpenseTable onDataChange={refreshCounts} groupByPic={true} />
        </TabsContent>

        <TabsContent value="relationships" className="mt-6">
          <ParentChildRelationshipManager onDataChange={refreshCounts} />
        </TabsContent>
      </Tabs>
    </div>
  );
};
